import React, { useState, useEffect } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import HandshakeIcon from "@mui/icons-material/Handshake";
import SaveIcon from "@mui/icons-material/Save";
import InfoIcon from "@mui/icons-material/Info";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import FlagIcon from "@mui/icons-material/Flag";
import HomeIcon from "@mui/icons-material/Home";
import CancelIcon from "@mui/icons-material/Cancel";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import { sendMessage } from "../services/websocketManager";
import { useWebSocket } from "../services/WebSocketProvider";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import { useTranslation } from "react-i18next";
import { GENERAL } from "../general/General";
import { useLocation } from "react-router-dom";
import { Share } from "./Share";
import Picker from "emoji-picker-react";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import GameInfoDialog from "./GameInfoDialog";
import AccountPage from "./AccountPage";
import SettingsPage from "./SettingsPage";
import Swal from "sweetalert2";
import SettingsIcon from "@mui/icons-material/Settings";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useMediaQuery, useTheme } from "@mui/material";

const ControlPanel = ({
  playerColorFromServer,
  gameIdFromServer,
  onCaptureBoard,
  setIsViewer,
  gameOver,
  onAccordionChange,
  onGameHistoryChange,
  getMoveHistory,
  aiLevel,
  gameInfo,
  winnerColor,
  learnMode,
  evaluation,
  showGameHistory,
  userData,
  topSubscription,
  backgroundColor = "transparent",
  expandedControlPanel = false,
}) => {
  const location = useLocation();
  const generalMessageViaState = location.state?.generalMessage;
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [playerColor, setPlayerColor] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [openSurrenderDialog, setOpenSurrenderDialog] = useState(false);
  const [currentGameUrl, setCurrentGameUrl] = useState(null);
  const [open, setOpen] = useState(false);
  const [viewer, setViewer] = useState(false);
  const [openOfferDrawDialog, setOpenOfferDrawDialog] = useState(false);
  const [offerDrawMessage, setOfferDrawMessage] = useState("");
  const [, setShowPicker] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [gameHistoryOpen, setGameHistoryOpen] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [gameName, setGameName] = useState("");
  const [showHistoryText, setShowHistoryText] = useState(t("Show History"));
  const [emojiDialogOpen, setEmojiDialogOpen] = useState(false);
  const [gameInfoDialogOpen, setGameInfoDialogOpen] = useState(false);
  const { setShowHistoryLimitDialog } = useWebSocket();
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const navigate = useNavigate();

  const handleSaveGameAction = () => {
    if (gameName.trim()) {
      const generalMessage = { ...generalMessageTemplate };
      generalMessage.gameInfo.gameId = gameIdFromServer;
      generalMessage.messageType = "save_game_request";
      generalMessage.gameInfo.gameTeamType = gameInfo
        ? gameInfo.gameTeamType
        : null;
      generalMessage.gameInfo.userType = gameInfo ? gameInfo.userType : null;
      generalMessage.gameInfo.color = playerColor;
      generalMessage.gameInfo.winnerColor = gameOver ? winnerColor : null;
      generalMessage.gameInfo.aiLevel = aiLevel;
      generalMessage.gameInfo.gameName = gameName.trim();
      generalMessage.gameInfo.simpleGameHistory = getMoveHistory();

      sendMessage(generalMessage);

      setGameName("");
      setDialogOpen(false);
    } else {
      Swal.fire({
        title: t("Error!"),
        text: t("Save_Game_Name_Required"),
        icon: "error",
        confirmButtonText: t("OK"),
      });
    }
  };

  const handleShowGameInfo = () => {
    setExpanded(false);
    if (onAccordionChange) {
      onAccordionChange(false);
    }
    setGameInfoDialogOpen(true);
  };

  const handleShowHistory = () => {
    setExpanded(false);
    if (onAccordionChange) {
      onAccordionChange(false);
    }
    setGameHistoryOpen(!gameHistoryOpen);
    if (onGameHistoryChange) {
      onGameHistoryChange(gameHistoryOpen);
      if (gameHistoryOpen === true) {
        setShowHistoryText(t("Hide History"));
      } else {
        setShowHistoryText(t("Show History"));
      }
    }
  };

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
    if (onAccordionChange) {
      onAccordionChange(isExpanded);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOfferDraw = () => {
    setExpanded(false);
    if (onAccordionChange) {
      onAccordionChange(false);
    }
    if (generalMessageViaState?.playAgainstAI === true) {
      //setOfferDrawMessage("Unable to offer draw to AI");
    } else {
      setOfferDrawMessage("");
    }
    setOpenOfferDrawDialog(true);
  };

  const handleOfferDrawClose = () => {
    setOpenOfferDrawDialog(false);
  };

  const handleOfferDrawConfirm = () => {
    const isAI =
      generalMessageViaState?.playAgainstAI === true &&
      generalMessageViaState?.gameInfo?.opponentType !== "AIasHuman";

    const rawEval = evaluation?.value ?? 0;

    let aiAcceptDrow = false;

    if (isAI && playerColorFromServer === "white" && rawEval > 200) {
      aiAcceptDrow = true;
    }
    if (isAI && playerColorFromServer === "black" && rawEval < -200) {
      aiAcceptDrow = true;
    }

    const generalMessage = { ...generalMessageTemplate };
    generalMessage.gameInfo.gameId = gameIdFromServer;

    const moves = localStorage.getItem("currentMoveIndex");
    const requiredMoves = 20;

    const message = t("draw_offer_move_limit", {
      current: moves,
      required: requiredMoves,
    });

    if (isAI && moves < requiredMoves) {
      Swal.fire({
        text: message,
        icon: "info",
        confirmButtonText: t("OK"),
      });
      setOpenOfferDrawDialog(false);
      return;
    }

    if (isAI && aiAcceptDrow === true) {
      generalMessage.messageType = "offer_draw_accept";
    } else if (isAI && aiAcceptDrow === false) {
      Swal.fire({
        text: t("ai_decline_draw_strong_position"),
        icon: "info",
        confirmButtonText: t("OK"),
      });
      setOpenOfferDrawDialog(false);
      return;
    } else {
      generalMessage.messageType = "offer_draw_request";
    }

    sendMessage(generalMessage);
    setOpenOfferDrawDialog(false);
  };

  useEffect(() => {
    if (userData) {
      //console.log(userData);
    }
  }, [userData]);

  useEffect(() => {
    if (setIsViewer) {
      setViewer(setIsViewer);
    }
  }, [setIsViewer]);

  useEffect(() => {
    if (playerColorFromServer != null) {
      setPlayerColor(playerColorFromServer);
    }
  }, [playerColorFromServer]);

  useEffect(() => {
    if (gameIdFromServer != null) {
      setGameId(gameIdFromServer);
    }
  }, [gameIdFromServer]);

  useEffect(() => {
    if (showGameHistory === true) {
      setShowHistoryText(t("Hide History"));
    } else {
      setShowHistoryText(t("Show History"));
    }
    setGameHistoryOpen(gameHistoryOpen);
  }, [gameHistoryOpen, showGameHistory, t]);

  const handleEmojiDialogOpen = () => {
    setExpanded(false);
    if (onAccordionChange) {
      onAccordionChange(false);
    }
    setEmojiDialogOpen(true);
  };

  const handleEmojiDialogClose = () => {
    setEmojiDialogOpen(false);
  };

  const handleEmojiClick = (emojiData) => {
    setExpanded(false);
    if (onAccordionChange) {
      onAccordionChange(false);
    }
    const selectedEmoji = emojiData.emoji;

    const emojiMessage = { ...generalMessageTemplate };
    emojiMessage.messageType = "emoji";
    emojiMessage.game.gameId = location.state?.generalMessage.game.gameId;
    emojiMessage.gameInfo.gameId = location.state?.generalMessage.game.gameId;
    emojiMessage.color = playerColor;
    emojiMessage.content = selectedEmoji;

    sendMessage(emojiMessage);

    setShowPicker(false);
  };

  const handleSaveGame = () => {
    setExpanded(false);
    if (onAccordionChange) {
      onAccordionChange(false);
    }
    setDialogOpen(true);
  };

  const handleInviteViewer = () => {
    setExpanded(false);
    if (onAccordionChange) {
      onAccordionChange(false);
    }

    if (topSubscription) {
      if (topSubscription.viewerAccess === false) {
        Swal.fire({
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: t("Upgrade your subscription"),
          text: t("no_viewer_access"),
          cancelButtonText: t("Cancel"),
        }).then((result) => {
          if (result.isConfirmed) {
            setShowHistoryLimitDialog(true);
          } else {
            handleClose();
          }
        });
      } else {
        let gameURL = GENERAL.HOST + "invited/" + gameId + "/" + playerColor;
        setCurrentGameUrl(gameURL);
        handleOpen();
      }
    }
  };

  const handleShareScreen = () => {
    setExpanded(false);
    if (onAccordionChange) {
      onAccordionChange(false);
    }
    onCaptureBoard();
  };

  const handleLeave = () => {
    handleSurrender();
  };

  const handleSurrender = () => {
    setExpanded(false);
    if (onAccordionChange) {
      onAccordionChange(false);
    }
    if (gameOver === false && gameIdFromServer) {
      setOpenSurrenderDialog(true);
    } else {
      navigate("/main");
    }
  };

  const confirmSurrender = () => {
    const leaveMessage = { ...generalMessageTemplate };
    leaveMessage.messageType = "leave_game";
    leaveMessage.game.gameId = gameId;
    leaveMessage.color = playerColor;
    sendMessage(leaveMessage);
    setOpenSurrenderDialog(false);
  };

  // Improved button configurations with better, more readable gradients
  const buttons = [
    {
      label: t("Offer Draw"),
      icon: <HandshakeIcon />,
      gradient: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
      hoverGradient: "linear-gradient(135deg, #3730a3 0%, #6d28d9 100%)",
      action: handleOfferDraw,
      disabled: learnMode || viewer || gameOver,
      category: "game"
    },
    {
      label: t("Save Game"),
      icon: <SaveIcon />,
      gradient: "linear-gradient(135deg, #0891b2 0%, #0284c7 100%)",
      hoverGradient: "linear-gradient(135deg, #0e7490 0%, #0369a1 100%)",
      action: handleSaveGame,
      disabled: !gameOver,
      category: "game"
    },
    {
      label: t("Invite Viewer"),
      icon: <VisibilityIcon />,
      gradient: "linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)",
      hoverGradient: "linear-gradient(135deg, #db2777 0%, #e11d48 100%)",
      action: handleInviteViewer,
      disabled: learnMode || viewer || gameOver,
      category: "social"
    },
    {
      label: t("Share Screen"),
      icon: <CameraAltIcon />,
      gradient: "linear-gradient(135deg, #06b6d4 0%, #0ea5e9 100%)",
      hoverGradient: "linear-gradient(135deg, #0891b2 0%, #0284c7 100%)",
      action: handleShareScreen,
      disabled: false,
      category: "social"
    },
    {
      label: showHistoryText,
      icon: <HistoryIcon />,
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      hoverGradient: "linear-gradient(135deg, #047857 0%, #065f46 100%)",
      action: handleShowHistory,
      disabled: false,
      category: "info"
    },
    {
      label: t("Send Smile"),
      icon: <EmojiEmotionsIcon />,
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      hoverGradient: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
      action: handleEmojiDialogOpen,
      disabled: learnMode || viewer || gameOver,
      category: "social"
    },
    {
      label: t("Game Info"),
      icon: <InfoIcon />,
      gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
      hoverGradient: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
      action: handleShowGameInfo,
      disabled: false,
      category: "info"
    },
    {
      label: t("Show Settings"),
      icon: <SettingsIcon />,
      gradient: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
      hoverGradient: "linear-gradient(135deg, #475569 0%, #334155 100%)",
      action: () => setSettingsDialogOpen(true),
      disabled: false,
      category: "system"
    },
    {
      label: t("Show Account"),
      icon: <InfoIcon />,
      gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
      hoverGradient: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
      action: () => setAccountDialogOpen(true),
      disabled: false,
      category: "system"
    },
    {
      label: t("Surrender"),
      icon: <FlagIcon />,
      gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      hoverGradient: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
      action: handleSurrender,
      disabled: learnMode || viewer || gameOver,
      category: "game"
    },
    {
      label: t("Main"),
      icon: <HomeIcon />,
      gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      hoverGradient: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
      action: handleLeave,
      disabled: false,
      category: "system"
    },
    {
      label: t("Close"),
      icon: <CancelIcon />,
      gradient: "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)",
      hoverGradient: "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
      action: () => handleAccordionChange(null, false),
      disabled: false,
      category: "system"
    },
  ];

  return (
    <>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', color: 'white' }}>
          {t("Save_Game")}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("Save_Game_Name")}
            type="text"
            fullWidth
            value={gameName}
            onChange={(e) => setGameName(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#4f46e5',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4f46e5',
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#ef4444' }}>
            {t("Cancel")}
          </Button>
          <Button onClick={handleSaveGameAction} sx={{ 
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(135deg, #3730a3 0%, #6d28d9 100%)',
            }
          }}>
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={accountDialogOpen}
        onClose={() => setAccountDialogOpen(false)}
        maxWidth="md"
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            color: "white"
          }
        }}
      >
        <DialogContent>
          <AccountPage purchasedPackages={userData?.coinsPackages || []}/>
        </DialogContent>
      </Dialog>

      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            color: "white"
          }
        }}
      >
        <DialogContent>
          <SettingsPage />
        </DialogContent>
      </Dialog>

      <Accordion
        expanded={expanded}
        onChange={handleAccordionChange}
        sx={{
          position: "sticky",
          zIndex: 100,
          left: "0%",
          transform: expanded ? "translateY(-80%)" : "translateY(0)",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          background: expanded 
            ? 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)' 
            : "transparent",
          backdropFilter: expanded ? "blur(20px)" : "none",
          boxShadow: expanded 
            ? "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)" 
            : "none",
          border: expanded ? "1px solid rgba(255, 255, 255, 0.2)" : "none",
          maxWidth: expanded ? (isMobile ? "100%" : "420px") : "32px",
          borderRadius: expanded ? "24px" : "16px",
          overflow: "hidden",
          "&::before": expanded ? {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
            zIndex: -1,
          } : {},
        }}
      >
        {expanded && (
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => {
              setExpanded(false);
              if (onAccordionChange) onAccordionChange(false);
            }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 200,
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              color: "#666",
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.3)',
                color: "#333",
              },
              transition: "all 0.3s ease",
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        
        <AccordionSummary
          id="control-panel-header"
          sx={{
            minWidth: "auto",
            padding: expanded ? "16px" : "4px 8px",
            justifyContent: "center",
            "& .MuiAccordionSummary-content": {
              margin: 0,
              justifyContent: "center",
            },
            transition: "all 0.3s ease",
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: expanded ? 2 : 0,
              padding: expanded ? "8px 16px" : "8px",
              borderRadius: "12px",
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              color: 'white',
              boxShadow: "0 8px 32px rgba(79, 70, 229, 0.3)",
              transition: "all 0.3s ease",
              '&:hover': {
                transform: "scale(1.05)",
                boxShadow: "0 12px 40px rgba(79, 70, 229, 0.4)",
              }
            }}
          >
            <DashboardIcon
              sx={{
                fontSize: expanded ? "24px" : "20px",
                transition: "all 0.3s ease",
              }}
            />
            {expanded && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "1.1rem",
                  opacity: expanded ? 1 : 0,
                  transition: "opacity 0.3s ease 0.1s",
                }}
              >
                {t("Control Panel")}
              </Typography>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ padding: expanded ? (isMobile ? "12px" : "20px") : "0" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: isMobile ? "6px" : "12px",
              opacity: expanded ? 1 : 0,
              transition: "opacity 0.3s ease 0.2s",
            }}
          >
            <Grid
              container
              spacing={isMobile ? 1 : 2}
              direction="column"
              alignItems="center"
              sx={{ width: "100%", maxWidth: isMobile ? "100%" : "360px" }}
            >
              {buttons.map((button, index) => (
                <Grid item key={index} xs={12} sx={{ width: "100%" }}>
                  <Button
                    variant="contained"
                    startIcon={button.icon}
                    onClick={button.action}
                    disabled={button.disabled}
                    sx={{
                      width: "100%",
                      height: isMobile ? "44px" : "56px",
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      fontWeight: 700,
                      letterSpacing: "0.5px",
                      background: button.disabled 
                        ? 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)'
                        : button.gradient,
                      color: button.disabled ? "#9ca3af" : "#fff",
                      border: button.disabled 
                        ? "1px solid rgba(209, 213, 219, 0.3)"
                        : "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: isMobile ? "12px" : "14px",
                      textTransform: "none",
                      textShadow: button.disabled ? "none" : "0 1px 2px rgba(0, 0, 0, 0.3)",
                      boxShadow: button.disabled 
                        ? "inset 0 1px 2px rgba(0, 0, 0, 0.1)"
                        : "0 4px 14px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      position: "relative",
                      overflow: "hidden",
                      backdropFilter: "blur(10px)",
                      "&:hover": button.disabled ? {} : {
                        background: button.hoverGradient,
                        transform: isMobile ? "translateY(-1px)" : "translateY(-3px)",
                        boxShadow: isMobile 
                          ? "0 4px 15px rgba(0, 0, 0, 0.2)" 
                          : "0 8px 25px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                        border: "1px solid rgba(255, 255, 255, 0.4)",
                      },
                      "&:active": button.disabled ? {} : {
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.2), inset 0 2px 4px rgba(0, 0, 0, 0.1)",
                      },
                      "& .MuiButton-startIcon": {
                        marginRight: isMobile ? "8px" : "14px",
                        filter: button.disabled ? "none" : "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))",
                        "& svg": {
                          fontSize: isMobile ? "1.1rem" : "1.3rem",
                        }
                      }
                    }}
                  >
                    <span style={{ position: "relative", zIndex: 2 }}>
                      {button.label}
                    </span>
                  </Button>
                </Grid>
              ))}
            </Grid>

            <GameInfoDialog
              open={gameInfoDialogOpen}
              onClose={() => setGameInfoDialogOpen(false)}
              gameInfo={generalMessageViaState?.gameInfo}
            />

            <Dialog 
              open={openOfferDrawDialog} 
              onClose={handleOfferDrawClose}
              PaperProps={{
                sx: {
                  borderRadius: "16px",
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
                  backdropFilter: "blur(20px)",
                }
              }}
            >
              <DialogTitle sx={{ 
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', 
                color: 'white',
                borderRadius: "16px 16px 0 0"
              }}>
                {t("Offer Draw")}
              </DialogTitle>
              <DialogContent sx={{ mt: 2 }}>
                {offerDrawMessage ? (
                  <Typography>{offerDrawMessage}</Typography>
                ) : (
                  <Typography>{t("Do you want to offer a draw?")}</Typography>
                )}
              </DialogContent>
              <DialogActions>
                {!offerDrawMessage && (
                  <>
                    <Button onClick={handleOfferDrawClose} sx={{ color: '#ef4444' }}>
                      {t("No")}
                    </Button>
                    <Button onClick={handleOfferDrawConfirm} sx={{ 
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #3730a3 0%, #6d28d9 100%)',
                      }
                    }}>
                      {t("Yes")}
                    </Button>
                  </>
                )}
                {offerDrawMessage && (
                  <Button onClick={handleOfferDrawClose} sx={{ 
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #3730a3 0%, #6d28d9 100%)',
                    }
                  }}>
                    {t("OK")}
                  </Button>
                )}
              </DialogActions>
            </Dialog>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Dialog 
        open={emojiDialogOpen} 
        onClose={handleEmojiDialogClose}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
            backdropFilter: "blur(20px)",
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
          color: 'white',
          borderRadius: "16px 16px 0 0"
        }}>
          {t("Pick an Emoji")}
        </DialogTitle>
        <DialogContent>
          <Picker onEmojiClick={handleEmojiClick} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmojiDialogClose} sx={{ 
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
            }
          }}>
            {t("Close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
            backdropFilter: "blur(20px)",
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #06b6d4 0%, #0ea5e9 100%)', 
          color: 'white',
          borderRadius: "16px 16px 0 0"
        }}>
          {t("Invite Viewer dialog")}
        </DialogTitle>
        <DialogContent>
          <Share currentUrl={currentGameUrl} />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openSurrenderDialog}
        onClose={() => setOpenSurrenderDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
            backdropFilter: "blur(20px)",
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', 
          color: 'white',
          borderRadius: "16px 16px 0 0"
        }}>
          {t("Confirm Surrender")}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>{t("Are you sure you want to surrender?")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSurrenderDialog(false)} sx={{ color: '#10b981' }}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={confirmSurrender}
            sx={{ 
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              }
            }}
          >
            {t("Confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ControlPanel;